import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormElement, FormLabel, FormInput, FormNumberInput, DragHandle, FormToggle } from "../components/StyledElements";
import { AppContext } from '../contexts/AppContext';
import { useModelOptions } from '../hooks/useModelOptions';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { NodeCard } from './NodeCard';
import { Icon } from "@iconify/react";
import { CloseButton } from '../components/CloseButton';
import { normalizeNodeInputs } from '../utils/nodeInputs';
import { DEFAULT_LLM_MODEL, buildLlmModelOptions, resolveNodeModel } from '../utils/modelRegistry';


export const LLMNodeCard = ({ onAdd = () => { }, style = {} }) => {
  return (
    <NodeCard title="LLM" description="Transform data using an AI language model" onAdd={() => onAdd(500)} style={style} />
  )
};

export const LLMNode = ({ id, data }) => {
  console.log("!!! LLM NODE DATA: ", data);
  const updateNodeInternals = useUpdateNodeInternals();
  const { selectedClient } = useContext(AppContext);
  // Registered models, so the name doesn't have to be remembered. Free text still works.
  // This is the raw registry list — the option building happens once, below.
  const { models: registeredModels, error: modelsError } = useModelOptions({
    clientId: selectedClient?.public_id ?? selectedClient?.id ?? null,
  });
  const modelListId = `llm-model-options-${id}`;
  // Whether the model field has already taken its initial value from the graph data.
  const seededModel = useRef(false);
  const modelOptions = buildLlmModelOptions(registeredModels);
  const [showOptions, setShowOptions] = useState(false);
  const [localData, setLocalData] = useState({
    inputCount: 1,
    inputs: [{ id: "input-1", label: "input" }],
    maxTokens: 250,
    temperature: 0.7,
    systemMessage: "",
    prompt: "",
    useChatHistory: false,
    useAgentMemory: false,
    useUserMemory: false,
    useSecondaryEndpoint: false,
  });

  console.log("!!! LLM NODE LOCAL DATA: ", localData);

  useEffect(() => {
    console.log("!!!MODEL: ", data.model);
    if (!data) {
      return;
    }

    // Captured before the state updater runs, which React may execute after this body.
    const firstPass = !seededModel.current;
    if (firstPass) {
      seededModel.current = true;
    }

    setLocalData((current) => ({
      useChatHistory: data.useChatHistory || false,
      useAgentMemory: data.useAgentMemory || false,
      useSecondaryEndpoint: data.useSecondaryEndpoint || false,
      // The model is taken from the graph data once. Afterwards the field belongs to the
      // user: re-applying the incoming value here is what made a cleared model spring back
      // to its previous value on the next render.
      model: resolveNodeModel({
        firstPass,
        graphModel: data.model,
        currentModel: current.model,
      }),
      maxTokens: data.maxTokens || 250,
      temperature: data.temperature || 0.7,
      systemMessage: data.systemMessage,
      prompt: data.prompt,
      // Keep `inputCount` and `inputs` consistent (generated graphs can send 0 with []).
      ...normalizeNodeInputs(data),
    }));

    // Seed the default once, so a node from an older graph still gets a working model
    // without the field being forced back afterwards.
    if (firstPass && !data.model) {
      data.onUpdateData?.(id, { model: DEFAULT_LLM_MODEL });
    }
    // `id` is stable for the life of the node; listing it keeps the lint rule honest.
  }, [data, id]);

  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "10px", border: "1px solid var(--main-border-color)", borderRadius: "8px", backgroundColor: "var(--content-item-bg)" }}>
      <CloseButton onClick={() => {
        data.onDelete(id);
      }} />
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <DragHandle />
        <FormLabel label="LLM" />
        <FormElement>
          <FormLabel label="Input Count" />
          <FormNumberInput
            value={localData?.inputCount ?? 1}
            onChange={(e) => {
              // Commit once after resizing, so lowering the count persists too.
              const desired = Math.max(0, Number(e.target.value) || 0);
              const next = normalizeNodeInputs(
                { inputs: localData.inputs.slice(0, desired), inputCount: desired },
                { truncate: true }
              );
              setLocalData({ ...localData, ...next });
              data.onUpdateData(id, next);
              updateNodeInternals(id);
            }}
            min={1}
            step={1}
          />
        </FormElement>
        {
          Array.from({ length: localData.inputCount }).map((_, index) => {
            const input = localData.inputs[index];

            return (
              // Anchored to its row: fixed offsets drifted into the node's content area.
              <div key={input?.id ?? index} style={{ position: "relative" }}>
                <FormElement>
                  <FormInput
                    placeholder={`Enter label for input ${index + 1}`}
                    value={input?.label ?? "input"}
                    onChange={(e) => {
                      const newLabel = e.target.value;
                      const oldInput = localData.inputs[index] ?? { id: `input-${index + 1}` };
                      const newInputs = [...localData.inputs];
                      newInputs[index] = { ...oldInput, label: newLabel };
                      setLocalData({ ...localData, inputs: newInputs });
                      data.onUpdateData(id, { inputs: newInputs });
                      data.onRenameInput?.(id, oldInput.id, newLabel);
                      console.log("!!! INPUT LABEL CHANGE: ", newInputs);
                      updateNodeInternals(id);
                    }}
                  />
                </FormElement>
                {input && (
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={input.id}
                    style={{ top: "50%", left: "-16px", height: "12px", width: "12px", zIndex: 10 }}
                  />
                )}
              </div>
            );
          })
        }

        <div style={{ display: "flex", flexDirection: "row", gap: "4px", alignItems: "center", zIndex: 100000, marginBottom: "4px" }}>
          <FormLabel label="Options" style={{ margin: 0 }} />
          <div style={{ marginLeft: "10px", justifyContent: "center", cursor: "pointer" }} onClick={() => setShowOptions(!showOptions)}>
            <Icon icon={showOptions ? "mdi:chevron-up" : "mdi:chevron-down"} style={{ fontSize: "32px", color: "var(--main-font-color)" }} />
          </div>
        </div>
        <div style={{ display: showOptions ? "flex" : "none", flexDirection: "column", gap: "10px" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "4px", justifyContent: "space-between" }}>
            <FormElement style={{ width: "50%" }}>
              <FormLabel label="Include Memory" />
              <FormToggle
                checked={localData?.useAgentMemory ?? false}
                onChange={(e) => {
                  setLocalData({ ...localData, useAgentMemory: e.target.checked });
                  data.onUpdateData(id, { useAgentMemory: e.target.checked });
                }}
              />
            </FormElement>
            <FormElement style={{ width: "50%" }}>
              <FormLabel label="Include Chat History" />
              <FormToggle
                checked={localData?.useChatHistory ?? false}
                onChange={(e) => {
                  setLocalData({ ...localData, useChatHistory: e.target.checked });
                  data.onUpdateData(id, { useChatHistory: e.target.checked });
                }}
              />
            </FormElement>
          </div>
          <div style={{ display: "none", flexDirection: "row", gap: "4px", justifyContent: "space-between" }}>
            <FormElement style={{ width: "50%" }}>
              <FormLabel label="Use Secondary Endpoint" />
              <FormToggle
                checked={localData?.useSecondaryEndpoint ?? false}
                onChange={(e) => {
                  setLocalData({ ...localData, useSecondaryEndpoint: e.target.checked });
                  data.onUpdateData(id, { useSecondaryEndpoint: e.target.checked });
                }}
              />
            </FormElement>
          </div>
          <FormElement>
            <FormLabel label="Model" />
            <FormInput
              list={modelListId}
              placeholder="Pick a registered model or type a name"
              value={localData?.model ?? ""}
              onChange={(e) => {
                console.log("!!! METHOD CHANGE: ", e.target.value);
                setLocalData({ ...localData, model: e.target.value });
                data.onUpdateData(id, { model: e.target.value });
              }}
            />
            <datalist id={modelListId}>
              {modelOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </datalist>
            <div style={{ fontSize: "12px", fontStyle: "italic", color: "#a0a6b1" }}>
              {modelsError
                ? `Model registry unavailable (${modelsError}) — the built-in defaults and typed names still work.`
                : "Choose a built-in, a registered endpoint, or type a name. Empty uses the environment deployment."}
            </div>
          </FormElement>
          <FormElement>
            <FormLabel label="Max Tokens" />
            <FormNumberInput
              value={localData?.maxTokens}
              onChange={(e) => {
                setLocalData({ ...localData, maxTokens: e.target.value });
                data.onUpdateData(id, { maxTokens: e.target.value });
              }}
              min={50}
            />
          </FormElement>
          <FormElement>
            <FormLabel label="Temperature" />
            <FormNumberInput
              value={localData.temperature}
              onChange={(e) => {
                console.log("!!! TEMPERATURE CHANGE: ", e.target.value);
                setLocalData({ ...localData, temperature: e.target.value });
                data.onUpdateData(id, { temperature: e.target.value });
              }}
              min={0}
              max={1}
              step={0.1}
            />
          </FormElement>
          <FormElement>
            <FormLabel label="System Message" />
            <FormInput
              as="textarea"
              rows={3}
              placeholder="Enter system message for the LLM"
              value={localData.systemMessage || ""}
              onChange={(e) => {
                setLocalData({ ...localData, systemMessage: e.target.value });
                data.onUpdateData(id, { systemMessage: e.target.value });
              }}
              style={{ width: "100%", fontFamily: "monospace" }}
            />
          </FormElement>
        </div>
        <FormElement>
          <FormLabel label="Prompt - {{ input }} can be used to pass the input" />
          <FormInput
            as="textarea"
            rows={5}
            placeholder="Enter prompt for the LLM"
            value={localData.prompt || ""}
            onChange={(e) => {
              setLocalData({ ...localData, prompt: e.target.value });
              data.onUpdateData(id, { prompt: e.target.value });
            }}
            style={{ width: "100%", fontFamily: "monospace" }}
          />
        </FormElement>
      </div>
      <Handle type="source" position={Position.Right} style={{ height: "12px", width: "12px" }} id="llm-output" />
    </div>
  );
};

export default LLMNode;
